import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CogIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { VariableRegistryService } from '../services/variableRegistryService';
import { useAuth } from '../contexts/AuthContext';
import { 
  ALL_VARIABLES, 
  VARIABLE_CATEGORIES, 
  VariableDefinition 
} from '../utils/variableRegistry';

const DocumentVariables = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [syncStatus, setSyncStatus] = useState<{
    missing: number;
    total: number;
    existing: number;
  } | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCheckingSync, setIsCheckingSync] = useState(false);

  // Szinkronizálási státusz ellenőrzése
  const checkSyncStatus = async () => {
    if (!user?.organizationId) return;
    
    setIsCheckingSync(true);
    try {
      const status = await VariableRegistryService.checkMissingVariables(user.organizationId);
      setSyncStatus({
        missing: status.missing.length,
        total: status.total,
        existing: status.existing
      });
      
      if (status.missing.length === 0) {
        toast.success('Minden registry változó szinkronizálva van!');
      } else {
        toast(`${status.missing.length} változó hiányzik a Firebase-ből`, { icon: 'ℹ️' });
      }
    } catch (error) {
      console.error('Hiba a sync státusz ellenőrzése során:', error);
      toast.error('Hiba a sync státusz ellenőrzése során');
    } finally {
      setIsCheckingSync(false);
    }
  };

  // Registry szinkronizálás
  const handleSyncRegistry = async () => {
    if (!user?.organizationId || !user?.id) return;
    
    setIsSyncing(true);
    try {
      await VariableRegistryService.syncRegistryToFirebase(user.organizationId, user.id);
      toast.success('Registry sikeresen szinkronizálva!');
      
      // Frissítjük a státuszt
      await checkSyncStatus();
    } catch (error) {
      console.error('Hiba a szinkronizálás során:', error);
      toast.error('Hiba a szinkronizálás során');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <CommonPageHeader
        title="Dokumentum Változók"
        subtitle="Variable Registry szinkronizálás a Firebase adatbázissal"
        icon={CogIcon}
      >
        <div className="flex flex-wrap items-center gap-3">
          {/* Registry szinkronizálás státusz */}
          {syncStatus && (
            <div className="flex items-center space-x-2 px-3 py-2 bg-blue-500/20 text-blue-100 rounded-lg text-sm">
              <span>Registry: {syncStatus.existing}/{syncStatus.total}</span>
              {syncStatus.missing > 0 && (
                <span className="bg-red-500 text-white px-2 py-1 rounded text-xs">
                  {syncStatus.missing} hiányzik
                </span>
              )}
            </div>
          )}

          <button
            onClick={checkSyncStatus}
            disabled={isCheckingSync}
            className="inline-flex items-center px-3 py-2 bg-white/20 dark:bg-gray-700/20 backdrop-blur-sm text-white border border-white/20 dark:border-gray-600 rounded-lg text-sm hover:bg-white/30 disabled:opacity-50"
          >
            {isCheckingSync ? (
              <div className="animate-spin h-4 w-4 mr-1 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <CogIcon className="h-4 w-4 mr-1" />
            )}
            Ellenőrzés
          </button>

          <button
            onClick={handleSyncRegistry}
            disabled={isSyncing}
            className="inline-flex items-center px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {isSyncing ? (
              <div className="animate-spin h-4 w-4 mr-1 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <SparklesIcon className="h-4 w-4 mr-1" />
            )}
            Szinkronizálás
          </button>
        </div>
      </CommonPageHeader>

      {/* Registry szinkronizálás */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Variable Registry szinkronizálás
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Szinkronizáld a változó registry-t a Firebase adatbázissal
          </p>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Szinkronizálás státusz */}
          {syncStatus && (
            <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Registry státusz: {syncStatus.existing}/{syncStatus.total} változó szinkronizálva
                </p>
                {syncStatus.missing > 0 && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    {syncStatus.missing} változó hiányzik a Firebase-ből
                  </p>
                )}
                {syncStatus.missing === 0 && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                    Minden változó szinkronizálva van ✓
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Műveletek */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={checkSyncStatus}
              disabled={isCheckingSync}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              {isCheckingSync ? (
                <div className="animate-spin h-4 w-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
              ) : (
                <CogIcon className="h-4 w-4 mr-2" />
              )}
              Státusz ellenőrzése
            </button>

            <button
              onClick={handleSyncRegistry}
              disabled={isSyncing}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {isSyncing ? (
                <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <SparklesIcon className="h-4 w-4 mr-2" />
              )}
              Registry szinkronizálás
            </button>
          </div>

          {/* Információ */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              ℹ️ Mit csinál a szinkronizálás?
            </h4>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
              <li>• Ellenőrzi a Variable Registry-ben definiált változókat</li>
              <li>• Eltávolítja a duplikált változókat a Firebase-ből</li>
              <li>• Hozzáadja a hiányzó változókat</li>
              <li>• Frissíti a megváltozott változókat</li>
              <li>• Biztosítja, hogy minden változó csak egyszer szerepeljen</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Registry változók megjelenítése */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Elérhető változók a Registry-ben
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Ezek a változók automatikusan elérhetők a háttérrendszerből ({ALL_VARIABLES.length} db)
          </p>
        </div>
        
        <div className="p-6">
          {VARIABLE_CATEGORIES.map(category => {
            const categoryVariables = ALL_VARIABLES.filter(v => v.category === category.id);
            if (categoryVariables.length === 0) return null;
            
            return (
              <div key={category.id} className="mb-8">
                <h4 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-3"
                    style={{ backgroundColor: category.color }}
                  />
                  {category.name} ({categoryVariables.length})
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryVariables.map(variable => (
                    <div 
                      key={variable.key}
                      className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                            {variable.name}
                          </h5>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono">
                            {variable.key}
                          </p>
                          {variable.description && (
                            <p className="text-xs text-gray-600 dark:text-gray-300 mt-2">
                              {variable.description}
                            </p>
                          )}
                          {variable.dataPath && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-mono">
                              {variable.dataPath}
                            </p>
                          )}
                        </div>
                        <div className="ml-3 flex flex-col items-end">
                          <span 
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                            style={{ 
                              backgroundColor: `${category.color}20`,
                              color: category.color 
                            }}
                          >
                            {variable.source}
                          </span>
                          <span className="text-xs text-gray-400 mt-1">
                            {variable.type}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DocumentVariables;